/*
  InnoFlex Odontología — datos comerciales de la página especializada.

  IMPORTANTE: esta estructura (`dentalPlans`) es INDEPENDIENTE de los planes
  generales de `src/pages/home/components/Pricing.tsx` y de los planes de
  dealers (`DealersPricing.tsx`). No se comparte ni se sobrescribe nada.

  Los precios son valores comerciales fijos por moneda (no hay conversión
  automática por API). La tasa de referencia usada para fijar los valores en
  RD$ es 1 USD ≈ 59 DOP, redondeando hacia abajo a la centena de 50 más
  cercana — la misma regla que usan los planes generales y los de dealers.
*/

export type DentalCurrency = 'USD' | 'DOP';
export type DentalPlanId = 'starter' | 'business' | 'pro' | 'premium';

/** Prefijo visible de cada moneda. */
export const DENTAL_CURRENCY_LABEL: Record<DentalCurrency, string> = {
  USD: 'US$',
  DOP: 'RD$',
};

export interface DentalCapacity {
  doctors: string;
  users: string;
  branches: string;
  cashboxes: string;
  chairs: string;
}

export interface DentalFeatureGroup {
  title: string;
  items: string[];
}

export interface DentalPlan {
  id: DentalPlanId;
  name: string;
  /** Insignia destacada (p. ej. "Más recomendado"). */
  badge: string | null;
  /** Muestra la palabra "Desde" antes del precio. */
  from: boolean;
  accent: string;
  accentDark: string;
  /** Tono oscuro del color del plan, legible sobre fondos claros (contraste AA). */
  accentInk: string;
  description: string;
  /** Mensualidad por moneda (valor comercial fijo). */
  monthly: Record<DentalCurrency, number>;
  /** Implementación única por moneda (valor comercial fijo). */
  implementation: Record<DentalCurrency, number>;
  capacity: DentalCapacity;
  /** Entre 7 y 10 funciones principales mostradas en la tarjeta. */
  highlights: string[];
  /** Listado completo mostrado en el modal "Ver todas las funciones". */
  allFeatures: DentalFeatureGroup[];
  ctaLabel: string;
  /** `form` abre el formulario de contacto existente; `advisor` abre WhatsApp. */
  ctaKind: 'form' | 'advisor';
  /** Nota comercial mostrada debajo del botón. */
  footnote?: string;
}

export const dentalPlans: DentalPlan[] = [
  {
    id: 'starter',
    name: 'Starter',
    badge: null,
    from: false,
    accent: '#38bdf8',
    accentDark: '#0284c7',
    accentInk: '#0369a1',
    description: 'Ideal para un doctor que quiere digitalizar y administrar su consultorio.',
    monthly: { USD: 34.99, DOP: 2050 },
    implementation: { USD: 150, DOP: 8850 },
    capacity: { doctors: '1', users: '1', branches: '1', cashboxes: '1', chairs: '1' },
    highlights: [
      'Pacientes y citas ilimitados con expediente completo',
      'Agenda clínica con disponibilidad y ausencias del doctor',
      'Historia médica: alergias, condiciones, medicamentos y signos vitales',
      'Evoluciones clínicas con historial versionado',
      'Odontograma digital y periodontograma',
      'Planes de tratamiento, presupuestos por pieza y plantillas',
      'Ortodoncia y tratamientos de larga duración con control de avance',
      'Recetas, indicaciones, fotografías y radiografías',
      'Caja completa: apertura, cobros, abonos, recibos y cierre',
      'Portal del paciente y portal del doctor',
    ],
    allFeatures: [
      {
        title: 'Pacientes y agenda',
        items: [
          'Registro completo de pacientes',
          'Pacientes y citas ilimitados',
          'Agenda clínica',
          'Disponibilidad, bloqueos y ausencias del doctor',
          'Tareas y seguimientos por paciente',
        ],
      },
      {
        title: 'Historia clínica',
        items: [
          'Historia médica y odontológica',
          'Alergias, condiciones, medicamentos y cirugías',
          'Signos vitales',
          'Evoluciones clínicas',
          'Versionado de la historia clínica',
          'Formularios digitales enviados al paciente por enlace',
          'Recetas e indicaciones',
          'Fotografías, radiografías y documentos',
        ],
      },
      {
        title: 'Odontograma y periodontograma',
        items: [
          'Odontograma digital (dentición permanente, temporal o mixta)',
          'Registro por pieza y por superficie',
          'Periodontograma',
        ],
      },
      {
        title: 'Tratamientos y presupuestos',
        items: [
          'Planes de tratamiento',
          'Presupuestos por procedimiento y pieza',
          'Plantillas de plan de tratamiento',
          'Ortodoncia y tratamientos de larga duración',
          'Balance por paciente',
        ],
      },
      {
        title: 'Caja',
        items: [
          'Apertura y cierre de caja',
          'Pagos, abonos y recibos',
          'Ingresos y egresos',
        ],
      },
      {
        title: 'Portales',
        items: ['Portal del paciente', 'Portal del doctor'],
      },
      {
        title: 'Reportes',
        items: ['Dashboard dental'],
      },
    ],
    ctaLabel: 'Comenzar con Starter',
    ctaKind: 'form',
  },
  {
    id: 'business',
    name: 'Business',
    badge: null,
    from: false,
    accent: '#06b6d4',
    accentDark: '#0e7490',
    accentInk: '#0e7490',
    description: 'Ideal para consultorios pequeños con recepción y varios doctores.',
    monthly: { USD: 69.99, DOP: 4100 },
    implementation: { USD: 250, DOP: 14750 },
    capacity: { doctors: '2', users: '3', branches: '1', cashboxes: '1', chairs: '2' },
    highlights: [
      'Todo lo incluido en Starter',
      'Ficha de doctores con agenda, ausencias y color propio',
      'Sillones y pantalla de consultorios en vivo',
      'Especialidades y subespecialidades',
      'Sala de espera, check-in y lista de espera',
      'Consentimientos con firma digital',
      'Laboratorio dental y proveedores',
      'Alertas del paciente visibles en consulta',
      'Inventario de insumos, lotes, vencimientos y compras',
      'Recordatorios de cita por WhatsApp y reportes por doctor',
    ],
    allFeatures: [
      { title: 'Base', items: ['Todo lo incluido en Starter'] },
      {
        title: 'Equipo clínico',
        items: [
          'Ficha del doctor',
          'Agenda por doctor y sillón',
          'Ausencias y bloqueos',
          'Color propio en la agenda',
          'Especialidades y subespecialidades',
        ],
      },
      {
        title: 'Sillones y recepción',
        items: [
          'Sillones y consultorios',
          'Consultorios en vivo',
          'Sala de espera y check-in',
          'Lista de espera',
        ],
      },
      {
        title: 'Documentación clínica',
        items: [
          'Consentimientos y firmas digitales',
          'Impresión de consentimientos e indicaciones',
          'Fotografías antes y después',
        ],
      },
      {
        title: 'Laboratorio dental',
        items: ['Casos de laboratorio', 'Proveedores de laboratorio'],
      },
      {
        title: 'Seguridad clínica',
        items: ['Alertas del paciente'],
      },
      {
        title: 'Inventario y compras',
        items: [
          'Inventario de insumos',
          'Alertas de stock bajo',
          'Lotes y vencimientos',
          'Consumo automático por procedimiento',
          'Compras y proveedores',
        ],
      },
      {
        title: 'Cobros y seguimiento',
        items: [
          'Tratamientos divididos por fases',
          'Planes de pago y cuotas',
          'Cuentas por cobrar',
          'Recordatorios de cita por WhatsApp',
          'Reportes por doctor, servicio y caja',
        ],
      },
    ],
    ctaLabel: 'Elegir Business',
    ctaKind: 'form',
  },
  {
    id: 'pro',
    name: 'Pro',
    badge: 'Más recomendado',
    from: false,
    accent: '#00cfff',
    accentDark: '#0891b2',
    accentInk: '#0e7490',
    description:
      'Ideal para clínicas establecidas que necesitan control administrativo, clínico y financiero.',
    monthly: { USD: 119.99, DOP: 7050 },
    implementation: { USD: 400, DOP: 23600 },
    capacity: { doctors: '4', users: '8', branches: '1', cashboxes: '2', chairs: '4' },
    highlights: [
      'Todo lo incluido en Business',
      'Seguros y ARS completo: coberturas, copagos, preautorizaciones y reclamaciones',
      'Tarifario por aseguradora',
      'Etiquetas, reglas automáticas y segmentos de pacientes',
      'NCF y facturación electrónica e-CF mediante integración',
      'Gastos, cuentas por pagar y flujo de caja',
      'Radiología y tomografía: solicitudes, estudios e informes',
      'Automatización de recordatorios y seguimiento de pacientes',
      'Rentabilidad por tratamiento, producción por doctor y por sillón',
      'Roles, permisos y auditoría',
    ],
    allFeatures: [
      { title: 'Base', items: ['Todo lo incluido en Business'] },
      {
        title: 'Seguros y ARS',
        items: [
          'Aseguradoras y ARS',
          'Coberturas, copagos y preautorizaciones',
          'Reclamaciones y seguimiento de pagos',
          'Tarifario por aseguradora',
        ],
      },
      {
        title: 'Pacientes y segmentación',
        items: [
          'Catálogo de etiquetas de paciente',
          'Reglas automáticas de etiquetado',
          'Segmentos guardados',
          'Dashboard de etiquetas',
        ],
      },
      {
        title: 'Finanzas y facturación',
        items: [
          'NCF y facturación electrónica e-CF mediante integración',
          'Notas de crédito y anulaciones',
          'Gastos',
          'Cuentas por pagar',
          'Flujo de caja',
          'Auditoría de facturación dental',
        ],
      },
      {
        title: 'Imágenes',
        items: [
          'Solicitudes y agenda de estudios',
          'Realización del estudio y carga de archivos',
          'Informe radiológico y entrega de resultados',
        ],
      },
      {
        title: 'Automatizaciones',
        items: [
          'Automatización de recordatorios',
          'Seguimiento de pacientes inactivos',
          'Seguimiento de presupuestos pendientes',
          'Recordatorios de pagos y cuotas',
        ],
      },
      {
        title: 'Reportes y control',
        items: [
          'Rentabilidad por tratamiento',
          'Producción por doctor',
          'Productividad por sillón',
          'Conversión de presupuestos',
          'Reportes gerenciales',
          'Exportación a Excel y PDF',
          'Roles y permisos avanzados',
          'Auditoría del sistema',
        ],
      },
    ],
    ctaLabel: 'Solicitar demostración',
    ctaKind: 'form',
  },
  {
    id: 'premium',
    name: 'Premium',
    badge: null,
    from: true,
    accent: '#14b8a6',
    accentDark: '#0f766e',
    accentInk: '#0f766e',
    description: 'Ideal para clínicas grandes, centros multisucursal y redes odontológicas.',
    monthly: { USD: 199.99, DOP: 11750 },
    implementation: { USD: 600, DOP: 35400 },
    capacity: { doctors: '8', users: '15', branches: '2', cashboxes: '3', chairs: '8' },
    highlights: [
      'Todo lo incluido en Pro',
      'Administración multisucursal y expediente unificado',
      'Agenda consolidada y dashboard corporativo',
      'Cajas, inventario y precios por sucursal',
      'Transferencias de inventario y compras centralizadas',
      'Listas de precios por sucursal',
      'Reportes financieros consolidados y comparación entre sucursales',
      'Campañas segmentadas, reactivación de pacientes y bot de WhatsApp con IA',
      'API de catálogo con llaves de acceso e integraciones a la medida',
      'Migración asistida, capacitación y ejecutivo de cuenta',
    ],
    allFeatures: [
      { title: 'Base', items: ['Todo lo incluido en Pro'] },
      {
        title: 'Multisucursal',
        items: [
          'Administración multisucursal',
          'Expediente unificado',
          'Agenda consolidada',
          'Dashboard corporativo',
          'Cajas por sucursal',
          'Inventario por sucursal',
          'Transferencias de inventario',
          'Compras centralizadas',
          'Precios por sucursal',
        ],
      },
      {
        title: 'Finanzas consolidadas',
        items: [
          'Consolidación de ingresos y gastos',
          'Reportes financieros consolidados',
          'Comparación entre sucursales',
          'Rentabilidad por sucursal',
        ],
      },
      {
        title: 'Control y gobierno',
        items: [
          'Roles corporativos',
          'Permisos personalizados por sucursal',
          'Flujos de aprobación',
          'Auditoría centralizada',
        ],
      },
      {
        title: 'Crecimiento y automatización',
        items: [
          'Campañas de WhatsApp segmentadas',
          'Reactivación de pacientes',
          'Bot de WhatsApp con IA',
          'Asistente con IA sobre los datos de la clínica',
          'Automatizaciones personalizadas',
          'Reportes personalizados',
          'Envío programado de reportes',
        ],
      },
      {
        title: 'Integraciones',
        items: [
          'API de catálogo con llaves de acceso',
          'Integraciones a la medida (cotizadas aparte)',
          'Integraciones con páginas web y proveedores externos',
        ],
      },
      {
        title: 'Servicio',
        items: [
          'Migración asistida',
          'Capacitación personalizada',
          'Soporte prioritario',
          'Ejecutivo de cuenta',
        ],
      },
    ],
    ctaLabel: 'Hablar con un asesor',
    ctaKind: 'advisor',
    footnote:
      '¿Necesitas más doctores, usuarios, cajas o sucursales? Podemos crear una configuración personalizada para tu organización.',
  },
];

/** Etiquetas de capacidad usadas en tarjetas y tabla comparativa. */
export const dentalCapacityMeta: {
  field: keyof DentalCapacity;
  icon: string;
  label: string;
  /** Etiqueta corta en plural para las tarjetas. */
  shortLabel: string;
  /** Etiqueta corta en singular (cuando la capacidad es 1). */
  shortLabelSingular: string;
}[] = [
  {
    field: 'doctors',
    icon: 'ri-user-heart-line',
    label: 'Doctores',
    shortLabel: 'doctores',
    shortLabelSingular: 'doctor',
  },
  {
    field: 'users',
    icon: 'ri-group-line',
    label: 'Usuarios totales',
    shortLabel: 'usuarios totales',
    shortLabelSingular: 'usuario total',
  },
  {
    field: 'branches',
    icon: 'ri-store-2-line',
    label: 'Sucursales',
    shortLabel: 'sucursales',
    shortLabelSingular: 'sucursal',
  },
  {
    field: 'cashboxes',
    icon: 'ri-cash-line',
    label: 'Cajas',
    shortLabel: 'cajas',
    shortLabelSingular: 'caja',
  },
  {
    field: 'chairs',
    icon: 'ri-calendar-2-line',
    label: 'Agendas o sillones',
    shortLabel: 'agendas o sillones',
    shortLabelSingular: 'agenda o sillón',
  },
];

/** Sección "Mucho más que una agenda odontológica". */
export const dentalBenefits: { icon: string; title: string; desc: string }[] = [
  {
    icon: 'ri-folder-user-line',
    title: 'Expediente clínico completo',
    desc: 'Historia médica y odontológica, evoluciones, recetas, fotografías, radiografías y documentos de cada paciente en un solo expediente.',
  },
  {
    icon: 'ri-tooth-line',
    title: 'Odontograma y periodontograma digital',
    desc: 'Registra el estado de cada pieza y del periodonto, con el historial de cambios visible durante la consulta.',
  },
  {
    icon: 'ri-file-list-3-line',
    title: 'Tratamientos y presupuestos',
    desc: 'Planes de tratamiento por fases y presupuestos por procedimiento y pieza, listos para aprobar y convertir en cobros.',
  },
  {
    icon: 'ri-cash-line',
    title: 'Caja, facturación y cuentas por cobrar',
    desc: 'Apertura y cierre de caja, pagos, abonos, recibos, balance por paciente y control de lo que falta por cobrar.',
  },
  {
    icon: 'ri-archive-2-line',
    title: 'Inventario y laboratorios',
    desc: 'Insumos con alertas de stock, lotes y vencimientos, consumo por procedimiento y trabajos enviados al laboratorio dental.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Seguros y ARS',
    desc: 'Coberturas, copagos, preautorizaciones, reclamaciones y seguimiento de los pagos de cada aseguradora.',
  },
  {
    icon: 'ri-whatsapp-line',
    title: 'WhatsApp y seguimiento de pacientes',
    desc: 'Recordatorios de citas, seguimiento de pacientes inactivos, presupuestos pendientes y recordatorios de pagos.',
  },
  {
    icon: 'ri-line-chart-line',
    title: 'Reportes de productividad y rentabilidad',
    desc: 'Producción por doctor, productividad por sillón, rentabilidad por tratamiento y conversión de presupuestos.',
  },
];

/** Sección "Todo el recorrido del paciente en una sola plataforma". */
export const dentalFlow: { icon: string; title: string }[] = [
  { icon: 'ri-smartphone-line', title: 'El paciente solicita una cita' },
  { icon: 'ri-calendar-check-line', title: 'Recepción agenda y confirma' },
  { icon: 'ri-file-edit-line', title: 'El paciente completa su historia clínica' },
  { icon: 'ri-folder-open-line', title: 'El doctor revisa el expediente' },
  { icon: 'ri-tooth-line', title: 'El doctor actualiza el odontograma' },
  { icon: 'ri-file-list-3-line', title: 'Se crea el tratamiento y presupuesto' },
  { icon: 'ri-quill-pen-line', title: 'El paciente firma y realiza un pago' },
  { icon: 'ri-cash-line', title: 'Caja registra el cobro' },
  { icon: 'ri-refresh-line', title: 'Inventario y finanzas se actualizan' },
  { icon: 'ri-heart-pulse-line', title: 'La clínica realiza el seguimiento' },
];

/** Reglas de capacidad explicadas debajo de los planes. */
export const dentalCapacityRules: { icon: string; text: string }[] = [
  { icon: 'ri-user-line', text: 'Cada usuario representa una cuenta individual.' },
  { icon: 'ri-user-heart-line', text: 'Los doctores forman parte del total de usuarios.' },
  { icon: 'ri-lock-2-line', text: 'Los usuarios no deben compartir contraseñas.' },
  { icon: 'ri-account-circle-line', text: 'Los pacientes del portal no cuentan como usuarios internos.' },
  { icon: 'ri-cash-line', text: 'Una caja representa un punto independiente de apertura, cobro y cierre.' },
  { icon: 'ri-calendar-2-line', text: 'Una agenda o sillón representa un recurso clínico que puede recibir citas.' },
  { icon: 'ri-store-2-line', text: 'Una sucursal representa una ubicación física.' },
  { icon: 'ri-infinity-line', text: 'Los pacientes y las citas son ilimitados en todos los planes.' },
  {
    icon: 'ri-apps-2-line',
    text: 'Cada plan activa un paquete de módulos. Dentro de un módulo activo, todas sus funciones vienen incluidas.',
  },
];

/* ── Tabla comparativa ─────────────────────────────────────────────────────
   Orden de valores: [Starter, Business, Pro, Premium]
   'check' = incluido · 'dash' = no incluido · texto = valor literal

   REGLA: cada fila tiene que ser algo que el sistema pueda activar o quitar
   de verdad, o sea un módulo de `PermissionModule` (el paquete del plan se
   define en Planes SaaS → Módulos) o una capacidad numérica que valida
   `PlanLimitService`. No partir sub-funciones de un mismo módulo en filas
   distintas: si un plan tiene `DentalInsurance`, tiene coberturas, copagos,
   preautorizaciones y reclamaciones — no se pueden vender por separado.
────────────────────────────────────────────────────────────────────────── */
export type DentalCell = 'check' | 'dash' | string;

export interface DentalCompareRow {
  label: string;
  values: [DentalCell, DentalCell, DentalCell, DentalCell];
}

export interface DentalCompareCategory {
  id: string;
  title: string;
  icon: string;
  rows: DentalCompareRow[];
}

export const dentalCompare: DentalCompareCategory[] = [
  {
    id: 'capacidad',
    title: 'Capacidad',
    icon: 'ri-bar-chart-grouped-line',
    rows: [
      { label: 'Doctores', values: ['1', 'Hasta 2', 'Hasta 4', 'Hasta 8'] },
      { label: 'Usuarios totales', values: ['1', '3', '8', '15'] },
      { label: 'Sucursales', values: ['1', '1', '1', 'Hasta 2'] },
      { label: 'Cajas', values: ['1', '1', '2', '3'] },
      { label: 'Agendas o sillones', values: ['1', '2', '4', '8'] },
      { label: 'Pacientes', values: ['Ilimitados', 'Ilimitados', 'Ilimitados', 'Ilimitados'] },
      { label: 'Citas', values: ['Ilimitadas', 'Ilimitadas', 'Ilimitadas', 'Ilimitadas'] },
    ],
  },
  {
    id: 'pacientes-agenda',
    title: 'Pacientes y agenda',
    icon: 'ri-calendar-check-line',
    rows: [
      { label: 'Registro completo de pacientes', values: ['check', 'check', 'check', 'check'] },
      { label: 'Pacientes y citas ilimitados', values: ['check', 'check', 'check', 'check'] },
      { label: 'Agenda clínica con disponibilidad, bloqueos y ausencias', values: ['check', 'check', 'check', 'check'] },
      { label: 'Tareas y seguimientos por paciente', values: ['check', 'check', 'check', 'check'] },
      { label: 'Ficha de doctores y agenda por doctor', values: ['dash', 'check', 'check', 'check'] },
      { label: 'Sillones y pantalla de consultorios en vivo', values: ['dash', 'check', 'check', 'check'] },
      { label: 'Especialidades y subespecialidades', values: ['dash', 'check', 'check', 'check'] },
      { label: 'Sala de espera, check-in y lista de espera', values: ['dash', 'check', 'check', 'check'] },
      { label: 'Alertas del paciente', values: ['dash', 'check', 'check', 'check'] },
      { label: 'Agenda consolidada multisucursal', values: ['dash', 'dash', 'dash', 'check'] },
    ],
  },
  {
    id: 'historia-clinica',
    title: 'Historia clínica',
    icon: 'ri-folder-user-line',
    rows: [
      { label: 'Historia médica y odontológica', values: ['check', 'check', 'check', 'check'] },
      { label: 'Alergias, condiciones, medicamentos y cirugías', values: ['check', 'check', 'check', 'check'] },
      { label: 'Signos vitales', values: ['check', 'check', 'check', 'check'] },
      { label: 'Evoluciones clínicas', values: ['check', 'check', 'check', 'check'] },
      { label: 'Versionado de la historia clínica', values: ['check', 'check', 'check', 'check'] },
      { label: 'Formularios digitales enviados al paciente por enlace', values: ['check', 'check', 'check', 'check'] },
      { label: 'Recetas e indicaciones', values: ['check', 'check', 'check', 'check'] },
      { label: 'Fotografías, radiografías y documentos', values: ['check', 'check', 'check', 'check'] },
      { label: 'Consentimientos y firmas digitales', values: ['dash', 'check', 'check', 'check'] },
      { label: 'Expediente unificado multisucursal', values: ['dash', 'dash', 'dash', 'check'] },
    ],
  },
  {
    id: 'odontograma',
    title: 'Odontograma y periodontograma',
    icon: 'ri-tooth-line',
    rows: [
      { label: 'Odontograma digital (permanente, temporal o mixta)', values: ['check', 'check', 'check', 'check'] },
      { label: 'Registro por pieza y por superficie', values: ['check', 'check', 'check', 'check'] },
      { label: 'Periodontograma', values: ['check', 'check', 'check', 'check'] },
    ],
  },
  {
    id: 'tratamientos',
    title: 'Tratamientos',
    icon: 'ri-file-list-3-line',
    rows: [
      { label: 'Planes de tratamiento', values: ['check', 'check', 'check', 'check'] },
      { label: 'Presupuestos por procedimiento y pieza', values: ['check', 'check', 'check', 'check'] },
      { label: 'Plantillas de plan de tratamiento', values: ['check', 'check', 'check', 'check'] },
      { label: 'Ortodoncia y tratamientos de larga duración', values: ['check', 'check', 'check', 'check'] },
      { label: 'Tratamientos por fases y planes de pago', values: ['dash', 'check', 'check', 'check'] },
      { label: 'Conversión de presupuestos', values: ['dash', 'dash', 'check', 'check'] },
    ],
  },
  {
    id: 'caja-facturacion',
    title: 'Caja y facturación',
    icon: 'ri-cash-line',
    rows: [
      { label: 'Apertura y cierre de caja', values: ['check', 'check', 'check', 'check'] },
      { label: 'Pagos, abonos y recibos', values: ['check', 'check', 'check', 'check'] },
      { label: 'Ingresos y egresos', values: ['check', 'check', 'check', 'check'] },
      { label: 'Balance por paciente', values: ['check', 'check', 'check', 'check'] },
      { label: 'Cuentas por cobrar', values: ['dash', 'check', 'check', 'check'] },
      { label: 'Gastos y cuentas por pagar', values: ['dash', 'dash', 'check', 'check'] },
      { label: 'Flujo de caja', values: ['dash', 'dash', 'check', 'check'] },
      { label: 'NCF y facturación electrónica e-CF (integración)', values: ['dash', 'dash', 'check', 'check'] },
      { label: 'Notas de crédito y anulaciones', values: ['dash', 'dash', 'check', 'check'] },
      { label: 'Auditoría de facturación dental', values: ['dash', 'dash', 'check', 'check'] },
      { label: 'Cajas por sucursal', values: ['dash', 'dash', 'dash', 'check'] },
    ],
  },
  {
    id: 'inventario',
    title: 'Inventario y compras',
    icon: 'ri-archive-2-line',
    rows: [
      { label: 'Inventario de insumos con lotes y vencimientos', values: ['dash', 'check', 'check', 'check'] },
      { label: 'Alertas de stock bajo', values: ['dash', 'check', 'check', 'check'] },
      { label: 'Consumo automático por procedimiento', values: ['dash', 'check', 'check', 'check'] },
      { label: 'Compras y proveedores', values: ['dash', 'check', 'check', 'check'] },
      { label: 'Inventario y transferencias por sucursal', values: ['dash', 'dash', 'dash', 'check'] },
      { label: 'Compras centralizadas', values: ['dash', 'dash', 'dash', 'check'] },
    ],
  },
  {
    id: 'laboratorio',
    title: 'Laboratorio dental',
    icon: 'ri-flask-line',
    rows: [
      { label: 'Casos de laboratorio y proveedores', values: ['dash', 'check', 'check', 'check'] },
    ],
  },
  {
    id: 'seguros',
    title: 'Seguros y ARS',
    icon: 'ri-shield-check-line',
    rows: [
      { label: 'Seguros y ARS: coberturas, copagos, preautorizaciones, reclamaciones y seguimiento de pagos', values: ['dash', 'dash', 'check', 'check'] },
      { label: 'Tarifario por aseguradora', values: ['dash', 'dash', 'check', 'check'] },
    ],
  },
  {
    id: 'portales',
    title: 'Portales',
    icon: 'ri-account-circle-line',
    rows: [
      { label: 'Portal del paciente', values: ['check', 'check', 'check', 'check'] },
      { label: 'Portal del doctor', values: ['check', 'check', 'check', 'check'] },
    ],
  },
  {
    id: 'radiologia',
    title: 'Radiología y tomografía',
    icon: 'ri-scan-2-line',
    rows: [
      { label: 'Radiografías y documentos en el expediente', values: ['check', 'check', 'check', 'check'] },
      { label: 'Solicitudes, estudios, informes y entrega de resultados', values: ['dash', 'dash', 'check', 'check'] },
    ],
  },
  {
    id: 'etiquetas',
    title: 'Etiquetas y segmentación',
    icon: 'ri-price-tag-3-line',
    rows: [
      { label: 'Catálogo de etiquetas de paciente', values: ['dash', 'dash', 'check', 'check'] },
      { label: 'Reglas automáticas de etiquetado', values: ['dash', 'dash', 'check', 'check'] },
      { label: 'Segmentos guardados y dashboard de etiquetas', values: ['dash', 'dash', 'check', 'check'] },
    ],
  },
  {
    id: 'whatsapp',
    title: 'WhatsApp y automatizaciones',
    icon: 'ri-whatsapp-line',
    rows: [
      { label: 'Recordatorios de cita por WhatsApp', values: ['dash', 'check', 'check', 'check'] },
      { label: 'Automatización de recordatorios y seguimiento de pacientes', values: ['dash', 'dash', 'check', 'check'] },
      { label: 'Campañas de WhatsApp segmentadas y reactivación', values: ['dash', 'dash', 'dash', 'check'] },
      { label: 'Bot de WhatsApp con IA', values: ['dash', 'dash', 'dash', 'check'] },
      { label: 'Asistente con IA sobre los datos de la clínica', values: ['dash', 'dash', 'dash', 'check'] },
    ],
  },
  {
    id: 'reportes',
    title: 'Reportes',
    icon: 'ri-line-chart-line',
    rows: [
      { label: 'Dashboard dental', values: ['check', 'check', 'check', 'check'] },
      { label: 'Reportes por doctor, servicio y caja', values: ['dash', 'check', 'check', 'check'] },
      { label: 'Rentabilidad por tratamiento y producción por doctor', values: ['dash', 'dash', 'check', 'check'] },
      { label: 'Productividad por sillón', values: ['dash', 'dash', 'check', 'check'] },
      { label: 'Reportes gerenciales y exportación a Excel y PDF', values: ['dash', 'dash', 'check', 'check'] },
      { label: 'Reportes financieros consolidados y comparación entre sucursales', values: ['dash', 'dash', 'dash', 'check'] },
      { label: 'Reportes personalizados y envío programado', values: ['dash', 'dash', 'dash', 'check'] },
    ],
  },
  {
    id: 'multisucursal',
    title: 'Multisucursal y control',
    icon: 'ri-building-2-line',
    rows: [
      { label: 'Roles, permisos y auditoría del sistema', values: ['dash', 'dash', 'check', 'check'] },
      { label: 'Administración multisucursal y dashboard corporativo', values: ['dash', 'dash', 'dash', 'check'] },
      { label: 'Listas de precios por sucursal', values: ['dash', 'dash', 'dash', 'check'] },
      { label: 'Roles corporativos y permisos por sucursal', values: ['dash', 'dash', 'dash', 'check'] },
      { label: 'Flujos de aprobación y auditoría centralizada', values: ['dash', 'dash', 'dash', 'check'] },
    ],
  },
  {
    id: 'integraciones',
    title: 'Integraciones',
    icon: 'ri-plug-line',
    rows: [
      { label: 'API de catálogo con llaves de acceso', values: ['dash', 'dash', 'dash', 'check'] },
      { label: 'Integraciones a la medida (cotizadas aparte)', values: ['dash', 'dash', 'dash', 'check'] },
    ],
  },
  {
    id: 'soporte',
    title: 'Soporte',
    icon: 'ri-customer-service-2-line',
    rows: [
      { label: 'Implementación y capacitación inicial', values: ['check', 'check', 'check', 'check'] },
      { label: 'Migración asistida', values: ['dash', 'dash', 'dash', 'check'] },
      { label: 'Capacitación personalizada', values: ['dash', 'dash', 'dash', 'check'] },
      { label: 'Soporte prioritario', values: ['dash', 'dash', 'dash', 'check'] },
      { label: 'Ejecutivo de cuenta', values: ['dash', 'dash', 'dash', 'check'] },
    ],
  },
];

/** Preguntas frecuentes (alimentan también el schema FAQPage). */
export const dentalFaq: { q: string; a: string }[] = [
  {
    q: '¿Los doctores cuentan como usuarios del plan?',
    a: 'Sí. Los doctores forman parte del total de usuarios de cada plan. Por ejemplo, el plan Business incluye hasta 2 doctores dentro de 3 usuarios totales, así que queda 1 usuario para recepción o administración.',
  },
  {
    q: '¿Los pacientes del portal consumen usuarios?',
    a: 'No. Los pacientes que acceden al portal del paciente no cuentan como usuarios internos. Los usuarios del plan son las cuentas del personal de la clínica.',
  },
  {
    q: '¿Hay límite de pacientes o de citas?',
    a: 'No. Los pacientes y las citas son ilimitados en todos los planes de InnoFlex Odontología, desde Starter hasta Premium.',
  },
  {
    q: '¿Qué es una caja y qué es una agenda o sillón?',
    a: 'Una caja es un punto independiente de apertura, cobro y cierre. Una agenda o sillón es un recurso clínico que puede recibir citas, por lo que dos sillones atendiendo en paralelo requieren dos agendas.',
  },
  {
    q: '¿El precio de implementación es mensual?',
    a: 'No. La implementación es un pago único y separado de la mensualidad. Cubre la configuración de la clínica, la carga inicial y la capacitación del equipo. Si pagas el año por adelantado, la implementación se te queda a mitad de precio.',
  },
  {
    q: '¿Cuánto ahorro si pago 6 meses o el año completo?',
    a: 'Pagando semestral obtienes 10% de descuento sobre los seis meses. Pagando anual obtienes 2 meses gratis: pagas 10 mensualidades y usas 12, alrededor de un 17% menos, además del 50% de descuento en la implementación. El descuento es permanente: el precio no sube en la renovación.',
  },
  {
    q: '¿Puedo crecer si necesito más doctores, cajas o sucursales?',
    a: 'Sí. El plan Premium parte de una capacidad base y se puede ampliar. Si necesitas más doctores, usuarios, cajas o sucursales creamos una configuración personalizada para tu organización.',
  },
];

/* ── Capturas reales del sistema ───────────────────────────────────────────
   Imágenes tomadas de InnoFlex Dental. Los datos de pacientes y doctores que
   aparecen en pantalla fueron sustituidos por datos de demostración antes de
   publicarlas: no contienen información personal real.
────────────────────────────────────────────────────────────────────────── */
export interface DentalScreen {
  id: string;
  label: string;
  icon: string;
  src: string;
  width: number;
  height: number;
  alt: string;
  caption: string;
}

export const dentalScreens: DentalScreen[] = [
  {
    id: 'agenda',
    label: 'Agenda',
    icon: 'ri-calendar-2-line',
    src: 'odontologia/agenda.png',
    width: 1920,
    height: 1080,
    alt: 'Agenda dental de InnoFlex con vista por día, filtros por doctor, consultorio y estado',
    caption:
      'Vista por día, semana o mes. Agrupa por doctor, consultorio o estado, y gestiona disponibilidad y bloqueos desde la misma pantalla.',
  },
  {
    id: 'consultorios',
    label: 'Consultorios en vivo',
    icon: 'ri-macbook-line',
    src: 'odontologia/consultorios.png',
    width: 1920,
    height: 1080,
    alt: 'Pantalla de consultorios en vivo mostrando el estado de cada box de la clínica',
    caption:
      'El estado de cada sillón en tiempo real: quién está en el consultorio y qué citas siguen. Se actualiza solo.',
  },
  {
    id: 'doctor',
    label: 'Ficha del doctor',
    icon: 'ri-user-heart-line',
    src: 'odontologia/doctor.png',
    width: 1920,
    height: 1080,
    alt: 'Ficha del doctor en InnoFlex con perfil, disponibilidad, ausencias, agenda y pacientes',
    caption:
      'Perfil profesional, disponibilidad, ausencias, agenda, pacientes asignados y accesos, con color propio en la agenda.',
  },
  {
    id: 'pacientes',
    label: 'Pacientes',
    icon: 'ri-group-line',
    src: 'odontologia/pacientes.png',
    width: 1920,
    height: 1080,
    alt: 'Listado de pacientes de la clínica con búsqueda, etiquetas y acciones rápidas',
    caption:
      'Búsqueda por nombre, cédula, teléfono o expediente, filtros por etiquetas y acciones rápidas: expediente, cita o WhatsApp.',
  },
  {
    id: 'expediente',
    label: 'Expediente',
    icon: 'ri-folder-user-line',
    src: 'odontologia/expediente.png',
    width: 1920,
    height: 1080,
    alt: 'Expediente del paciente con resumen, citas, odontograma, periodontograma y documentos',
    caption:
      'Todo el paciente en una pantalla: balance, próxima cita, seguro, notas clínicas, presupuestos, documentos y comunicación.',
  },
  {
    id: 'odontograma',
    label: 'Odontograma',
    icon: 'ri-tooth-line',
    src: 'odontologia/odontograma.png',
    width: 1920,
    height: 1080,
    alt: 'Odontograma digital de InnoFlex con un caso clínico marcado, resumen clínico y diagnóstico generado',
    caption:
      'Dentición permanente, temporal o mixta según la edad. Marca piezas completas o superficies, resume el caso (piezas afectadas, caries pendientes, trabajo realizado) y redacta el diagnóstico solo.',
  },
  {
    id: 'odontograma-diente',
    label: 'Registro por pieza',
    icon: 'ri-focus-3-line',
    src: 'odontologia/odontograma-diente.png',
    width: 1920,
    height: 1080,
    alt: 'Panel de registro de una pieza dental con superficies, hallazgos, endodoncia, prótesis y cirugía',
    caption:
      'Por pieza: superficies, hallazgos, endodoncia, prótesis, cirugía, implante y ortodoncia, con estado diagnosticado, planificado, en proceso o completado.',
  },
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: 'ri-line-chart-line',
    src: 'odontologia/dashboard.png',
    width: 1920,
    height: 1080,
    alt: 'Dashboard dental con citas, no-show, ingresos, cuentas por cobrar y producción por doctor',
    caption:
      'Citas, no-show, ingresos, cuentas por cobrar, conversión de presupuestos, pacientes inactivos y producción por doctor.',
  },
];
